import { useState, useRef, useEffect } from "react";
import { UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export function LRHandlers({ lr, users = [], onNavigate, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState((lr.handlers || []).map((h) => h.id));
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setSel((lr.handlers || []).map((h) => h.id)); }, [lr.handlers]);
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.patch(`/lrs/${lr.id}/handlers`, { handler_ids: sel });
      onSaved?.(lr.id, data.handlers);
      toast.success("Handlers updated");
      setOpen(false);
    } catch {
      toast.error("Could not update handlers");
    } finally {
      setSaving(false);
    }
  }

  const handlers = lr.handlers || [];

  return (
    <div className="relative flex items-center gap-1 flex-wrap" ref={ref}>
      {handlers.map((h) => (
        <button
          key={h.id}
          type="button"
          onClick={() => onNavigate?.(h.id)}
          className="text-[11px] px-2 py-0.5 rounded-full bg-[#EBE7DD] hover:bg-[#DED9CC] text-[#4A4842] transition-colors"
          title={`See everything ${h.name} is responsible for`}
          data-testid={`lr-handler-chip-${lr.id}-${h.id}`}
        >
          {h.name}
        </button>
      ))}
      {handlers.length === 0 && <span className="text-[11px] text-[#A09E96]">—</span>}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn-ghost !px-1.5 !py-0.5"
        title="Assign ITO handler(s)"
        data-testid={`lr-assign-handler-${lr.id}`}
      >
        <UserPlus className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="absolute z-30 top-full mt-1 left-0 w-60 bg-white border border-[#D8D6CE] shadow-lg p-2"
          data-testid={`lr-handler-popover-${lr.id}`}
        >
          <div className="text-[11px] uppercase tracking-wide text-[#68665E] px-1 mb-1">Responsible (ITO handlers)</div>
          <div className="max-h-56 overflow-auto">
            {users.length === 0 ? (
              <div className="text-xs text-[#A09E96] p-2">No users found.</div>
            ) : (
              users.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 px-1.5 py-1 text-sm hover:bg-[#F5F4F0] cursor-pointer"
                  data-testid={`lr-handler-option-${lr.id}-${u.id}`}
                >
                  <input type="checkbox" checked={sel.includes(u.id)} onChange={() => toggle(u.id)} />
                  <span className="flex-1 truncate">{u.name}</span>
                  <span className="text-[10px] text-[#A09E96]">{u.role}</span>
                </label>
              ))
            )}
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary w-full mt-2 !py-1 text-xs justify-center"
            data-testid={`lr-handler-save-${lr.id}`}
          >
            {saving ? "Saving…" : (<><Check className="w-3.5 h-3.5" /> Save</>)}
          </button>
        </div>
      )}
    </div>
  );
}
